import React from 'react';

/**
 * Approved identity board (1536 × 1024 raster). Every mark below is a true
 * crop of this artwork rendered through an SVG viewBox — the original pixels
 * are preserved and nothing is redrawn.
 */
const BOARD_SRC = 'https://cdn.magicpatterns.com/uploads/s5PtFCBpEFtTqdLj1FQrKb/black-comet-labs-concept-v5.png';
const BOARD_WIDTH = 1536;
const BOARD_HEIGHT = 1024;
type CropProps = {
  className?: string;
};
function BoardCrop({
  viewBox,
  className,
  label,
  knockoutId,
  knockoutMode = 'wordmark'
}: {
  viewBox: string;
  className?: string;
  label?: string;
  /**
   * When set, the board's light matte is converted to transparency by
   * luminance (source RGB preserved, dark artwork kept opaque) instead of
   * relying on multiply. Must be unique per page.
   */
  knockoutId?: string;
  /**
   * 'wordmark' keys out everything above ~85% luminance (black lettering only).
   * 'mascot' uses a narrow 85–92% ramp so the green eyes, gold star and
   * lavender ears stay fully opaque while only the pale board ground drops out.
   */
  knockoutMode?: 'wordmark' | 'mascot';
}) {
  const decorative = !label;
  return <svg viewBox={viewBox} className={className}
  // The approved artwork is raster with its own near-white ground.
  // Multiply blends it into Warm Cream without redrawing anything.
  style={knockoutId ? undefined : {
    mixBlendMode: 'multiply'
  }} preserveAspectRatio="xMidYMid meet" role={decorative ? 'presentation' : 'img'} aria-label={decorative ? undefined : label} aria-hidden={decorative || undefined} focusable="false" data-id="element-10" data-mp="1x6djjj8yeox2">
      {knockoutId ? <defs data-id="element-11" data-mp="1s9543qf6wyzi">
          {/* Filter region = the full board in user space, so nothing outside
              the source is sampled (no black frame) and no lettering clips. */}
          <filter id={knockoutId} filterUnits="userSpaceOnUse" x={0} y={0} width={BOARD_WIDTH} height={BOARD_HEIGHT} colorInterpolationFilters="sRGB" data-id="element-12" data-mp="zq84bjklmz81">
            <feColorMatrix in="SourceGraphic" type="matrix" values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0.2126 0.7152 0.0722 0 0" result="lumAlpha" data-id="element-13" data-mp="2ztsk1ckjijkq" />
            <feComponentTransfer in="lumAlpha" result="keyed" data-id="element-14" data-mp="38o6vt55v3bz3">
              {knockoutMode === 'mascot' ?
          // alpha = 1 at ≤85% luminance, 0 at ≥92%, linear between (clamped).
          <feFuncA type="linear" slope={-14.2857} intercept={13.1429} data-id="element-15" data-mp="2gv6f1mlwk8bj" /> : <feFuncA type="table" tableValues="1 1 1 1 1 1 0.9 0.1 0" data-id="element-16" data-mp="2kv6y5kjy57ox" />}
            </feComponentTransfer>
            <feComposite in="keyed" in2="SourceAlpha" operator="in" data-id="element-17" data-mp="6at6hdi0ril" />
          </filter>
        </defs> : null}
      <image href={BOARD_SRC} xlinkHref={BOARD_SRC} x={0} y={0} width={BOARD_WIDTH} height={BOARD_HEIGHT} filter={knockoutId ? `url(#${knockoutId})` : undefined} data-id="element-18" data-mp="54ufiiw085bh" />
    </svg>;
}

/** Original heavy geometric BLACK COMET / spaced LABS wordmark. */
export function Wordmark({
  className,
  knockoutId
}: CropProps & {
  knockoutId?: string;
}) {
  return <BoardCrop viewBox="110 642 845 170" className={className} label="Black Comet Labs" knockoutId={knockoutId} data-id="element-19" data-mp="1ip9deoqgwd6" />;
}

/**
 * Small seated mascot for the logo lockups: the exact approved cat and gold
 * star from the board, tightly cropped. Decorative — the adjacent wordmark
 * carries the accessible name.
 */
export function LogoMascot({
  className,
  knockoutId
}: CropProps & {
  knockoutId: string;
}) {
  return <BoardCrop viewBox="260 32 605 596" className={className} knockoutId={knockoutId} knockoutMode="mascot" data-id="element-20" data-mp="3eq8v5l35a5es" />;
}

/** The cat with the Comet Gold tail star. */
export function CatMark({
  className
}: CropProps) {
  return <BoardCrop viewBox="252 26 638 603" className={className} data-id="element-21" data-mp="1jzr3f27l9380" />;
}

/** Complete primary lockup: cat, star and wordmark together. */
export function PrimaryLockup({
  className
}: CropProps) {
  return <BoardCrop viewBox="100 26 860 788" className={className} label="Black Comet Labs" data-id="element-22" data-mp="1b0454pa5odj6" />;
}

/** Four-point Comet Gold star motif. */
export function CometStar({
  className
}: CropProps) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false" data-id="element-23" data-mp="1199hphl6b7w5">
      <path d="M12 0c1.1 8.2 2.7 10.9 12 12-9.3 1.1-10.9 3.8-12 12-1.1-8.2-2.7-10.9-12-12C9.3 10.9 10.9 8.2 12 0Z" fill="currentColor" data-id="element-24" data-mp="16080cntmwi2k" />
    </svg>;
}